import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";

const authHeader = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const BROADCAST_KINDS = [
  { id: "info", label: "Info", emoji: "📣" },
  { id: "success", label: "Success", emoji: "✅" },
  { id: "warn", label: "Warn", emoji: "⚠️" },
  { id: "party", label: "Party", emoji: "🎉" },
];

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl bg-[#161324] border border-white/5 p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className="font-display text-3xl mt-1" style={{color: "var(--uno-yellow)"}}>{value}</div>
      {hint && <div className="text-xs text-white/40 mt-1">{hint}</div>}
    </div>
  );
}

export default function Admin() {
  const { user, refresh } = useAuth();
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  // Broadcast form
  const [bMessage, setBMessage] = useState("");
  const [bKind, setBKind] = useState("party");

  // Gift all form
  const [giftAllAmount, setGiftAllAmount] = useState(500);

  // Gift single (modal)
  const [giftTarget, setGiftTarget] = useState(null); // user object
  const [giftAmount, setGiftAmount] = useState(500);

  // Self topup
  const [topupAmount, setTopupAmount] = useState(1000);

  const load = async () => {
    setLoading(true);
    try {
      const [u, a, s] = await Promise.all([
        api.get("/admin/users", { headers: authHeader() }),
        api.get("/admin/actions", { headers: authHeader() }),
        api.get("/admin/stats", { headers: authHeader() }),
      ]);
      setUsers(u.data);
      setActions(a.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line
  }, [user]);

  const filteredUsers = useMemo(() => {
    if (!q) return users;
    const s = q.toLowerCase();
    return users.filter(u =>
      (u.name || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.user_id || "").toLowerCase().includes(s)
    );
  }, [users, q]);

  if (user && !user.is_admin) return <Navigate to="/lobby" replace/>;

  const broadcast = async () => {
    if (!bMessage.trim()) { toast.error("Enter a message"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/broadcast", { message: bMessage, kind: bKind, duration_ms: 8000 }, { headers: authHeader() });
      toast.success(`Reached ${r.data.reached} live connections`);
      setBMessage("");
      load();
    } catch (e) { toast.error("Broadcast failed"); }
    finally { setBusy(false); }
  };

  const giftAll = async () => {
    if (!giftAllAmount) return;
    if (!window.confirm(`Give ${giftAllAmount} coins to EVERY user?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/gift-all", { amount: Number(giftAllAmount) }, { headers: authHeader() });
      toast.success(`Sent ${giftAllAmount} to ${r.data.affected} users`);
      load();
    } catch (e) { toast.error("Gift-all failed"); }
    finally { setBusy(false); }
  };

  const gift = async (target, amount) => {
    setBusy(true);
    try {
      await api.post("/admin/gift", { identifier: target.email || target.user_id, amount: Number(amount) }, { headers: authHeader() });
      toast.success(`+${amount} → ${target.name}`);
      setGiftTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gift failed"); }
    finally { setBusy(false); }
  };

  const selfTopup = async () => {
    if (!topupAmount) return;
    setBusy(true);
    try {
      await api.post("/admin/self-topup", { amount: Number(topupAmount) }, { headers: authHeader() });
      toast.success(`+${topupAmount} coins`);
      await refresh();
      load();
    } catch (e) { toast.error("Top-up failed"); }
    finally { setBusy(false); }
  };

  const actionLabel = (a) => {
    if (a.type === "gift_coins") return `Gifted ${a.amount > 0 ? "+" : ""}${a.amount} to ${a.target_name || a.target_id}`;
    if (a.type === "self_topup") return `Self top-up +${a.amount}`;
    if (a.type === "gift_all") return `Gifted ${a.amount > 0 ? "+" : ""}${a.amount} to all (${a.affected})`;
    if (a.type === "broadcast") return `Broadcast: "${a.message}"`;
    return a.type;
  };

  return (
    <div className="min-h-screen" data-testid={ADMIN.container}>
      <TopNav/>
      <main className="max-w-6xl mx-auto px-4 sm:px-10 py-6 sm:py-10 relative z-10">
        <div className="mb-6 sm:mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--uno-red)] mb-2">Admin</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter">The house always wins.</h1>
            <p className="text-white/60 mt-2 text-sm">Signed in as <span className="text-white/90">{user?.email}</span>.</p>
          </div>
          <div className="glass px-4 py-2 rounded-full flex items-center gap-2">
            <span className="text-lg">🪙</span>
            <span className="font-display text-2xl" style={{color:"var(--uno-yellow)"}}>{user?.coins ?? 0}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total users" value={stats.total_users ?? "–"}/>
          <StatCard label="Live now" value={stats.online_users ?? "–"} hint="Global WS"/>
          <StatCard label="Active rooms" value={stats.active_rooms ?? "–"}/>
          <StatCard label="Paid" value={stats.paid_transactions ?? "–"} hint="Stripe"/>
        </div>

        {/* Broadcast + Gift all */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Broadcast */}
          <div className="rounded-2xl bg-[#161324] border border-white/5 p-5">
            <div className="flex items-center gap-2">
              <span className="text-xl">📣</span>
              <div className="font-display text-xl">Broadcast a message</div>
            </div>
            <p className="text-white/60 text-sm mt-1">Push a banner to every online user's screen — live.</p>
            <textarea
              data-testid="admin-broadcast-input"
              value={bMessage}
              onChange={(e) => setBMessage(e.target.value)}
              maxLength={500}
              placeholder="Announce something legendary…"
              rows={3}
              className="mt-3 w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[color:var(--uno-yellow)] resize-none"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {BROADCAST_KINDS.map(k => (
                <button
                  key={k.id}
                  onClick={() => setBKind(k.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${bKind === k.id ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
                >{k.emoji} {k.label}</button>
              ))}
            </div>
            <button
              data-testid="admin-broadcast-btn"
              onClick={broadcast}
              disabled={busy || !bMessage.trim()}
              className="mt-4 px-6 py-2.5 rounded-full bg-[color:var(--uno-red)] text-white font-semibold disabled:opacity-40"
            >Send to everyone</button>
          </div>

          {/* Gift-all + Self topup */}
          <div className="rounded-2xl bg-[#161324] border border-white/5 p-5 grid grid-cols-1 gap-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl">🌍</span>
                <div className="font-display text-xl">Gift every user coins</div>
              </div>
              <p className="text-white/60 text-sm mt-1">Confetti moment. Everyone gets an equal drop.</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-2xl">🪙</span>
                <input
                  data-testid="admin-gift-all-input"
                  type="number"
                  value={giftAllAmount}
                  onChange={(e) => setGiftAllAmount(e.target.value)}
                  className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-[color:var(--uno-yellow)]"
                />
                <div className="flex gap-1">
                  {[100, 500, 1000].map(v => (
                    <button key={v} onClick={() => setGiftAllAmount(v)} className="px-2.5 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs">{v}</button>
                  ))}
                </div>
              </div>
              <button
                data-testid="admin-gift-all-btn"
                onClick={giftAll}
                disabled={busy || !giftAllAmount}
                className="mt-4 w-full py-2.5 rounded-full bg-gradient-to-r from-[color:var(--uno-red)] via-[color:var(--uno-yellow)] to-[color:var(--uno-green)] text-black font-bold disabled:opacity-40"
              >Rain coins on everyone 🎉</button>
            </div>

            <div className="pt-5 border-t border-white/5">
              <div className="flex items-center gap-2">
                <span className="text-xl">🪙</span>
                <div className="font-display text-xl">Free top-up (you)</div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input
                  data-testid={ADMIN.selfTopupInput}
                  type="number"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-[color:var(--uno-yellow)]"
                />
                <button
                  data-testid={ADMIN.selfTopupBtn}
                  onClick={selfTopup}
                  disabled={busy}
                  className="px-5 py-2.5 rounded-full bg-[color:var(--uno-green)] text-black font-semibold disabled:opacity-50"
                >Top up</button>
              </div>
            </div>
          </div>
        </div>

        {/* User list */}
        <div className="rounded-2xl bg-[#161324] border border-white/5 overflow-hidden mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-white/5">
            <div>
              <div className="font-display text-lg">Users</div>
              <div className="text-[11px] text-white/40">Click any user to gift a custom amount</div>
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="w-full sm:w-64 bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-[color:var(--uno-yellow)]"
            />
          </div>
          <div className="grid grid-cols-12 px-4 sm:px-6 py-2 text-[10px] uppercase tracking-[0.2em] text-white/40 border-b border-white/5 bg-black/20">
            <div className="col-span-5 sm:col-span-4">User</div>
            <div className="hidden sm:block sm:col-span-3">Email</div>
            <div className="col-span-3 sm:col-span-2 text-right">Coins</div>
            <div className="col-span-4 sm:col-span-3 text-right">Quick gift</div>
          </div>
          {loading && <div className="p-6 text-white/50">Loading…</div>}
          <div className="max-h-[420px] overflow-y-auto">
            {filteredUsers.map(u => (
              <div key={u.user_id} className="grid grid-cols-12 px-4 sm:px-6 py-2.5 items-center border-b border-white/5 last:border-b-0 hover:bg-white/[0.03]">
                <button
                  onClick={() => { setGiftTarget(u); setGiftAmount(500); }}
                  className="col-span-5 sm:col-span-4 flex items-center gap-3 min-w-0 text-left"
                >
                  <div className="relative shrink-0">
                    <img src={u.avatar || u.picture} alt="" className="w-8 h-8 rounded-full border border-white/10"/>
                    {u.is_online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-[#161324]"/>}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate flex items-center gap-1">
                      {u.name}
                      {u.is_admin && <span className="text-[9px] uppercase tracking-widest text-[color:var(--uno-red)]">admin</span>}
                    </div>
                  </div>
                </button>
                <div className="hidden sm:block sm:col-span-3 text-xs text-white/60 truncate">{u.email}</div>
                <div className="col-span-3 sm:col-span-2 text-right font-display text-base" style={{color:"var(--uno-yellow)"}}>{u.coins ?? 0}</div>
                <div className="col-span-4 sm:col-span-3 flex justify-end gap-1">
                  {[100, 500, 2000].map(v => (
                    <button key={v} onClick={() => gift(u, v)} className="px-2 py-1 rounded-full bg-white/5 hover:bg-[color:var(--uno-green)] hover:text-black text-[11px] font-semibold transition-colors">+{v}</button>
                  ))}
                  <button onClick={() => { setGiftTarget(u); setGiftAmount(500); }} className="px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 text-[11px]">…</button>
                </div>
              </div>
            ))}
            {!loading && filteredUsers.length === 0 && (
              <div className="p-6 text-white/50 text-center">No users match.</div>
            )}
          </div>
        </div>

        {/* Action log */}
        <div className="rounded-2xl bg-[#161324] border border-white/5 overflow-hidden">
          <div className="px-4 sm:px-6 py-3 border-b border-white/5">
            <div className="font-display text-lg">Recent actions</div>
            <div className="text-[11px] text-white/40">Last 30 admin events</div>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {actions.length === 0 && <div className="p-4 text-white/50 text-sm">No actions yet.</div>}
            {actions.map((a, i) => (
              <div key={i} className="flex items-center justify-between px-4 sm:px-6 py-2 border-b border-white/5 last:border-b-0 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs uppercase tracking-widest text-white/40 shrink-0">
                    {a.type === "broadcast" ? "📣" : a.type === "gift_all" ? "🌍" : a.type === "self_topup" ? "🪙" : "🎁"}
                  </span>
                  <span className="truncate">{actionLabel(a)}</span>
                </div>
                <span className="text-xs text-white/40 shrink-0 ml-3">{new Date(a.at).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Custom gift modal */}
      <AnimatePresence>
        {giftTarget && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setGiftTarget(null)}>
            <motion.div initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-2xl p-6 w-full max-w-md">
              <div className="flex items-center gap-3 mb-4">
                <img src={giftTarget.avatar || giftTarget.picture} className="w-12 h-12 rounded-full border border-white/20"/>
                <div>
                  <div className="font-display text-xl">Gift {giftTarget.name}</div>
                  <div className="text-xs text-white/60">{giftTarget.email}</div>
                </div>
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Amount</div>
              <input
                type="number"
                value={giftAmount}
                onChange={(e) => setGiftAmount(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[color:var(--uno-yellow)] font-display text-2xl text-center"
                autoFocus
              />
              <div className="mt-3 flex flex-wrap gap-2 justify-center">
                {[-500, -100, 100, 500, 1000, 5000].map(v => (
                  <button key={v} onClick={() => setGiftAmount(v)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ${v < 0 ? "bg-[color:var(--uno-red)]/20 text-[color:var(--uno-red)] hover:bg-[color:var(--uno-red)]/30" : "bg-white/5 hover:bg-white/10"}`}>
                    {v > 0 ? "+" : ""}{v}
                  </button>
                ))}
              </div>
              <div className="mt-5 flex gap-3 justify-end">
                <button onClick={() => setGiftTarget(null)} className="px-4 py-2 rounded-full text-sm bg-white/10 hover:bg-white/20">Cancel</button>
                <button
                  onClick={() => gift(giftTarget, giftAmount)}
                  disabled={busy || !giftAmount}
                  className="px-5 py-2 rounded-full bg-[color:var(--uno-green)] text-black text-sm font-semibold disabled:opacity-50"
                >Send</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
